import React, { Component } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components/macro';
import { get, isEmpty, size, throttle } from 'lodash';
import { FocusableParent } from './navigation';
import List from 'components/List';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import HorizontalList from 'components/HorizontalList';
import BackButton from './BackButton';
import Button from './Button';
import scaleSize from '../utils/scale-size';
import config, { LIST_ITEMS_SIZE } from '../config';
import withMouseControls from './HOCs/withMouseControls';
import withScrollHack from './HOCs/withScrollHack';
import PackageItem from './Profile/PackageItem';

const SLOW_TRANSITION = config.mainTransitionDuration;

const CollectionWrapper = styled.div`
  padding-left: ${scaleSize(220)}px;
  height: ${scaleSize(1080)}px;
`;

const CollectionListsWrapper = styled.div`
  height: ${scaleSize(1080)}px;
  transform: translate3d(0, -${({ listOffset }) => listOffset}px, 0);
  transition: ${({ noAnimation }) =>
    config.withAnimation && !noAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
  position: relative;

  &.seasons {
    padding-top: ${scaleSize(150)}px;
    position: relative;
  }

  &.user-packages {
    padding-top: ${scaleSize(150)}px;
    position: relative;
  }
`;

const CollectionTitle = styled.div`
  height: ${scaleSize(150)}px;
  box-sizing: border-box;
  padding-top: ${scaleSize(70)}px;
  font-weight: 500;
  font-size: ${scaleSize(50)}px;
  line-height: 1.2;
  letter-spacing: normal;
  color: ${({ theme }) => theme.defaultColor};
  font-family: RobotoMedium, sans-serif;
  position: absolute;
  top: 0;
`;

const StyledBackButton = styled(BackButton)`
  z-index: 4;
  color: ${({ theme, buttonOffset }) => (buttonOffset > 540 ? theme.activeColor : theme.detailsTitleTextColor)};
  transform: translate3d(0, ${({ buttonOffset }) => scaleSize(buttonOffset)}px, 0);
  transition: ${config.withAnimation ? `transform ${SLOW_TRANSITION}, color ${SLOW_TRANSITION}` : 'none'};
`;

const StyledHorizontalList = styled(HorizontalList)`
  height: ${scaleSize(712)}px;
  // transform: translateY(${({ extraOffset }) => extraOffset || 0}px);
  transform: translate3d(0, ${({ extraOffset }) => extraOffset || 0}px, 0);
  margin-left: 0;
  position: relative;
  white-space: nowrap;
`;

const StyledMoreButton = styled(FocusableParent)`
  width: ${scaleSize(1480)}px;
  margin-top: ${scaleSize(40)}px;
  text-align: center;
`;

const onWheelHandler = throttle((deltaY) => {
  const direction = deltaY > 0 ? 'down' : 'up';
  SpatialNavigationWrapper.move(direction);
}, 300);

const MoreButton = ({ title, up, onEnter }) => {
  return (
    <StyledMoreButton sectionName={'MORE_BUTTON'} config={{ preventMouseNavigateIn: true, leaveFor: { up } }}>
      <Button onEnter={onEnter} focusOnMouseEnter>
        {title}
      </Button>
    </StyledMoreButton>
  );
};

const listConfig = {
  enterTo: 'last-focused',
  leaveFor: {
    right: '',
  },
  debounce: {
    time: 200,
    options: {
      leading: true,
      trailing: false,
      maxWait: 200,
    },
  },
};
// Разные типы данных приходят
// TODO: fix it
const getListSize = (currentList) =>
  get(currentList, ['content', 'data', 'content', 'length']) || get(currentList, ['content', 'content', 'length']);

class Collection extends Component {
  state = {
    listOffset: 0,
    focusedListIndex: 0,
    focusedItemId: null,
    // Позволяет отключить временно анимацию скролла
    // Полезно для начального рендера листа с не нулевой позицией
    noAnimation: false,
  };

  componentDidUpdate(prevProps) {
    const isNewDataCome = prevProps.data !== this.props.data;
    if (isNewDataCome) {
      // Убирает анимацию прокрутки при перерисовке с новыми данными
      this.setState({ noAnimation: true });
      setTimeout(() => this.setState({ noAnimation: false }));
    }
  }

  onListItemFocus = ({ listIndex, itemId, itemIndex }, listOffset) => {
    const { data, loadExtraListItems, collectionType } = this.props;
    const currentList = data[listIndex];
    // Для сезонов нет доп загрузки
    // + если есть кнопка "смотреть больше" доп загрузок тодже не должно быть
    if (collectionType !== 'seasons' && currentList.hideShowMore) {
      const listSize = getListSize(currentList);
      const isRequiresExtraLoad = itemIndex >= listSize - 1 - 4;
      if (isRequiresExtraLoad) loadExtraListItems(currentList.listId);
    }
    this.setState({
      focusedListIndex: listIndex,
      focusedItemId: itemId,
      listOffset: Math.max(0, listOffset + 60), //todo разобраться, что это за Magic Number!
    });
  };

  onListItemEnter = (item, payload) => {
    const { onItemEnter } = this.props;

    if (onItemEnter) onItemEnter(item, payload);
  };

  onBack = () => {
    const { history } = this.props;

    history.goBack();
  };

  // Это временны бридж от старой коллекции к новому листу
  _renderPackgesList(list, listIndex) {
    const { focusedListIndex } = this.state;
    const extraOffset = listIndex - focusedListIndex === 1 ? scaleSize(48) : 0;
    const isLastList = size(this.props.data.length) - 1 === listIndex;

    if (!list.items.length) return null;

    return (
      <StyledHorizontalList
        key={`list-${list.listId}`}
        listId={`package-list-${list.listId}`}
        extraOffset={extraOffset}
        sectionName={`list-${listIndex}`}
        defaultSection={listIndex === 0}
        startScrollIndex={1}
        onFocus={(item, index) => {
          this.onListItemFocus({ listIndex, itemId: item.id, itemIndex: index }, 0);
        }}
        onEnter={this.onListItemEnter}
        title={list.title}
        items={list.items}
        itemSize={LIST_ITEMS_SIZE.PACKAGE_CONTENT.width}
        itemComponent={PackageItem}
        config={{
          ...listConfig,
          leaveFor: {
            right: '',
            down: isLastList ? '@MORE_BUTTON' : `@list-${listIndex + 1}`,
          },
        }}
      />
    );
  }

  render() {
    const { listOffset, focusedListIndex, noAnimation } = this.state;
    const { data, collectionTitle, className, collectionType, withBackButton, moreButton } = this.props;

    const seasonsListOffset = focusedListIndex > 0 ? scaleSize(80) : 0;

    return (
      <CollectionWrapper className={className} onWheel={(e) => onWheelHandler(e.deltaY)}>
        {withBackButton && (
          <StyledBackButton onEnter={this.onBack} onFocus={this.onBackButtonFocus} defaultSection={isEmpty(data)} />
        )}
        <CollectionListsWrapper
          noAnimation={noAnimation}
          className={collectionType}
          listOffset={
            collectionType === 'seasons' || collectionType === 'user-packages'
              ? focusedListIndex * scaleSize(712) + seasonsListOffset
              : listOffset
          }
        >
          {collectionTitle && <CollectionTitle>{collectionTitle}</CollectionTitle>}
          {data.map((list, index) => {
            if (collectionType === 'user-packages') {
              return this._renderPackgesList(list, index);
            }
            // It fixes focus with different sizes of list
            const leaveFor = {
              up: data[index - 1] ? `@list-${index - 1}` : 'default',
              down: data[index + 1] ? `@list-${index + 1}` : 'default',
              right: '',
            };

            return (
              <List
                sectionName={`list-${index}`}
                key={`list-${list.listId}`}
                defaultSection={index === 0}
                onListItemFocus={this.onListItemFocus}
                onListItemEnter={this.onListItemEnter}
                listIndex={index}
                focusedListIndex={focusedListIndex}
                config={{
                  ...listConfig,
                  leaveFor,
                }}
                collectionType={collectionType}
                noAnimation={noAnimation}
                {...list}
              />
            );
          })}
          {moreButton && <MoreButton {...moreButton} up={`@list-${data.length - 1}`} />}
        </CollectionListsWrapper>
      </CollectionWrapper>
    );
  }
}

Collection.defaultProps = {
  withBackButton: true,
  data: [],
};

Collection.propTypes = {
  loadExtraListItems: PropTypes.func,
  withBackButton: PropTypes.bool,
  data: PropTypes.array,
  collectionType: PropTypes.string,
  onItemEnter: PropTypes.func,
};

export default compose(withRouter, withScrollHack, withMouseControls)(Collection);
